import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import CookieConsent from "@/components/CookieConsent";
import { ArrowRight, Shield, Zap, Globe, Layers, TrendingUp, Building2, Cpu, Sprout, Radio, ShoppingCart } from "lucide-react";

const LOGO_URL = "/logo.png";
const HERO_BG = "https://images.unsplash.com/photo-1773666330599-bf7afde8fbf7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZSUyMGRhcmslMjBibHVlfGVufDB8fHx8MTc3NTM2NTc4NXww&ixlib=rb-4.1.0&q=85";

const SECTOR_IMAGES = {
  trade: "https://images.unsplash.com/photo-1691591765923-3bd6f12f4209?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBzaGlwcGluZyUyMGNvbnRhaW5lciUyMHBvcnR8ZW58MHx8fHwxNzc1MzY1NzcxfDA&ixlib=rb-4.1.0&q=85",
  energy: "https://images.unsplash.com/photo-1770810416523-bf40d844a248?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzb2xhciUyMHBhbmVscyUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzUzNjU3NzF8MA&ixlib=rb-4.1.0&q=85",
  telecom: "https://images.unsplash.com/photo-1764151825324-4be510cd1e0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHx0ZWxlY29tJTIwdG93ZXIlMjBsYW5kc2NhcGUlMjBzdW5zZXR8ZW58MHx8fHwxNzc1MzY1Nzg1fDA&ixlib=rb-4.1.0&q=85",
  agriculture: "https://images.unsplash.com/photo-1763416160482-c77fadd32d3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMG1vZGVybiUyMGZhcm0lMjB0cmFjdG9yfGVufDB8fHx8MTc3NTM2NTc4NXww&ixlib=rb-4.1.0&q=85",
};

const SECTORS = [
  { key: "trade", title: "Trade & Supply Chain Finance", desc: "Unlocking liquidity across African trade corridors through structured, asset-backed financing solutions.", icon: TrendingUp, img: SECTOR_IMAGES.trade },
  { key: "energy", title: "Energy & Infrastructure", desc: "Financing renewable energy, power, and critical infrastructure to drive industrial expansion.", icon: Zap, img: SECTOR_IMAGES.energy },
  { key: "telecom", title: "Telecommunications", desc: "Investing in connectivity infrastructure and digital economy platforms across the continent.", icon: Radio, img: SECTOR_IMAGES.telecom },
  { key: "agriculture", title: "Agriculture & FMCG", desc: "Supporting agricultural value chains and fast-moving consumer goods distribution networks.", icon: Sprout, img: SECTOR_IMAGES.agriculture },
  { key: "fintech", title: "Financial Services & Fintech", desc: "Enabling inclusive access to capital through innovative financial technology solutions.", icon: Building2, img: null },
];

const LAYERS = [
  { icon: TrendingUp, title: "Financial Engine", desc: "Trade finance, credit, and structured finance solutions driving capital deployment and cash flow." },
  { icon: Zap, title: "Payments & Fintech", desc: "Transaction processing, cross-border settlements, and embedded financial services." },
  { icon: Shield, title: "Identity & Data", desc: "Digital identity, KYC/KYB frameworks, and credit intelligence systems for compliant operations." },
  { icon: Layers, title: "Asset Tokenization", desc: "Digitization and fractionalization of real-world assets using blockchain technology." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="Berkshire Africa" className="w-12 h-12 object-contain" />
            <span className="font-playfair text-lg font-bold text-[#0f172a] tracking-tight hidden sm:block">
              Berkshire Africa Inc
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button
                data-testid="nav-sign-in-button"
                className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-manrope font-semibold text-sm px-6 h-9 rounded-sm transition-colors duration-200"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center pt-16" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0f172a]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-blue-300 font-manrope mb-6 animate-fade-in-up">
              Investment & Financial Infrastructure
            </p>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-none mb-6 animate-fade-in-up animation-delay-100">
              Powering Africa's <br className="hidden sm:block" />
              Economic Future
            </h1>
            <p className="text-slate-300 text-base md:text-lg font-manrope leading-relaxed mb-10 max-w-2xl animate-fade-in-up animation-delay-200">
              Berkshire Africa Inc is a technology-enabled investment platform designed to unlock,
              finance, and scale economic activity across Africa and frontier markets.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/login">
                <Button
                  data-testid="hero-get-started-button"
                  className="bg-white text-[#1e3a8a] hover:bg-slate-100 font-manrope font-semibold text-sm px-8 h-11 rounded-sm transition-colors duration-200"
                >
                  Access Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10 animate-fade-in-up animation-delay-400">
            {[
              { label: "Target Capital", value: "$50M+" },
              { label: "Active Markets", value: "15+" },
              { label: "Key Sectors", value: "5" },
              { label: "Scaling To", value: "$500M" },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-playfair text-2xl sm:text-3xl font-bold text-white" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {s.value}
                </p>
                <p className="text-xs tracking-[0.15em] uppercase text-slate-400 font-manrope font-medium mt-1">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1e3a8a] font-manrope mb-4">About Us</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] tracking-tight">
                A New Category of Platform
              </h2>
            </div>
            <div className="md:col-span-7 space-y-5">
              <p className="text-slate-600 text-base font-manrope leading-relaxed">
                Headquartered in Delaware, United States, with an operational footprint spanning Africa and the Middle East,
                Berkshire has been purpose-built to operate at the intersection of capital markets, technology infrastructure,
                and real economy value chains.
              </p>
              <p className="text-slate-600 text-base font-manrope leading-relaxed">
                The platform integrates financial innovation with deep market access to address structural inefficiencies
                that continue to constrain growth across the continent. Berkshire combines <strong className="text-slate-800">capital deployment,
                transaction infrastructure, and digital enablement</strong> to originate, structure, finance, and digitize economic flows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Sectors */}
      <section className="py-20 md:py-28 bg-[#f8fafc]" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1e3a8a] font-manrope mb-4">Investment Focus</p>
            <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] tracking-tight">
              Key Sectors
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Top row - 2 large cards */}
            {SECTORS.slice(0, 2).map((sector, i) => (
              <div
                key={sector.key}
                data-testid={`sector-card-${sector.key}`}
                className={`md:col-span-6 group relative overflow-hidden rounded-sm border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-${(i + 1) * 100}`}
              >
                {sector.img && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={sector.img} alt={sector.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <sector.icon className="w-5 h-5 text-[#1e3a8a]" />
                    <h3 className="font-manrope font-bold text-lg text-[#0f172a]">{sector.title}</h3>
                  </div>
                  <p className="text-slate-500 text-sm font-manrope leading-relaxed">{sector.desc}</p>
                </div>
              </div>
            ))}
            {/* Bottom row - 3 smaller cards */}
            {SECTORS.slice(2).map((sector, i) => (
              <div
                key={sector.key}
                data-testid={`sector-card-${sector.key}`}
                className={`md:col-span-4 group relative overflow-hidden rounded-sm border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-${(i + 3) * 100}`}
              >
                {sector.img && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={sector.img} alt={sector.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <sector.icon className="w-5 h-5 text-[#1e3a8a]" />
                    <h3 className="font-manrope font-bold text-base text-[#0f172a]">{sector.title}</h3>
                  </div>
                  <p className="text-slate-500 text-sm font-manrope leading-relaxed">{sector.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Architecture */}
      <section className="py-20 md:py-28 bg-white" data-testid="platform-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1e3a8a] font-manrope mb-4">Technology</p>
            <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] tracking-tight">
              Platform Architecture
            </h2>
            <p className="text-slate-500 text-base font-manrope mt-4 max-w-2xl mx-auto">
              A multi-layered architecture capturing value across the full lifecycle of economic activity.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LAYERS.map((layer, i) => (
              <div
                key={layer.title}
                data-testid={`platform-layer-${i}`}
                className={`p-6 border border-slate-200 rounded-sm bg-slate-50/50 hover:bg-white hover:shadow-md hover:-translate-y-1 transition-all duration-300 animate-fade-in-up animation-delay-${(i + 1) * 100}`}
              >
                <div className="w-10 h-10 rounded-sm bg-[#1e3a8a]/10 flex items-center justify-center mb-4">
                  <layer.icon className="w-5 h-5 text-[#1e3a8a]" />
                </div>
                <h3 className="font-manrope font-bold text-base text-[#0f172a] mb-2">{layer.title}</h3>
                <p className="text-slate-500 text-sm font-manrope leading-relaxed">{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28 bg-[#0f172a]" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="animate-fade-in-up">
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-blue-400 font-manrope mb-4">Vision</p>
              <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">
                Africa's Leading Financial Infrastructure
              </h2>
              <p className="text-slate-400 text-base font-manrope leading-relaxed">
                Building a future where financial access, transaction efficiency, and asset liquidity are no longer constraints
                to growth, but enablers of scalable and inclusive economic development across the continent.
              </p>
            </div>
            <div className="animate-fade-in-up animation-delay-200">
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-blue-400 font-manrope mb-4">Mission</p>
              <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">
                Unlocking Capital & Infrastructure
              </h2>
              <p className="text-slate-400 text-base font-manrope leading-relaxed">
                Bridging persistent financing gaps, digitizing financial ecosystems, structuring institutional-grade
                investment vehicles, and enabling secure, compliant transactions across borders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Globe className="w-10 h-10 text-[#1e3a8a] mx-auto mb-6" />
          <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f172a] tracking-tight mb-4">
            Shaping the Future of Africa's Economy
          </h2>
          <p className="text-slate-500 text-base font-manrope leading-relaxed mb-8">
            Berkshire Africa represents a new category of platform that seamlessly integrates capital, technology,
            and execution capability to unlock economic value at scale.
          </p>
          <Link to="/login">
            <Button
              data-testid="cta-access-button"
              className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-manrope font-semibold text-sm px-8 h-11 rounded-sm transition-colors duration-200"
            >
              Access Portfolio Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f172a] border-t border-slate-800 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Berkshire Africa" className="w-12 h-12 object-contain" />
              <span className="font-playfair text-base font-bold text-white tracking-tight">Berkshire Africa Inc</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400 font-manrope">
              <span>Delaware, USA</span>
              <span className="text-slate-600">|</span>
              <span>Africa & Middle East</span>
            </div>
            <p className="text-slate-500 text-xs font-manrope">
              2025 Berkshire Africa Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}
